/**
 * FeedbackContext - Sound effects and haptic feedback management
 *
 * Manages audio playback, haptic vibrations, and user preferences
 * for feedback effects throughout the vault dashboard.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';

// Sound effect types
export type SoundEffect =
  | 'tick'           // Chart scrubbing tick
  | 'pop'            // Button press, selection
  | 'success'        // Successful action
  | 'celebration'    // ATH, milestone achieved
  | 'streak'         // Winning streak notification
  | 'refresh'        // Pull to refresh complete
  | 'error'          // Error notification
  | 'coin'           // Money/value increase
  | 'swoosh';        // Transition sound

// Haptic feedback types
export type HapticType =
  | 'light'          // Light tap
  | 'medium'         // Medium impact
  | 'heavy'          // Heavy impact
  | 'success'        // Success pattern
  | 'warning'        // Warning pattern
  | 'error'          // Error pattern
  | 'selection';     // Selection change

// Feedback state
export interface FeedbackState {
  soundEnabled: boolean;
  hapticEnabled: boolean;
  celebrationsEnabled: boolean;
  volume: number; // 0-1
  isAudioLoaded: boolean;
}

// Context type
interface FeedbackContextType extends FeedbackState {
  // Sound controls
  playSound: (effect: SoundEffect) => void;
  setSoundEnabled: (enabled: boolean) => void;
  setVolume: (volume: number) => void;

  // Haptic controls
  triggerHaptic: (type: HapticType) => void;
  setHapticEnabled: (enabled: boolean) => void;

  // Celebration controls
  setCelebrationsEnabled: (enabled: boolean) => void;

  // Combined feedback (sound + haptic)
  feedback: (sound: SoundEffect, haptic: HapticType) => void;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

// Custom hook to use feedback context
export const useFeedback = (): FeedbackContextType => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
};

// Sound file paths (relative to public folder)
const SOUND_FILES: Record<SoundEffect, string> = {
  tick: '/sounds/tick.mp3',
  pop: '/sounds/pop.mp3',
  success: '/sounds/success.mp3',
  celebration: '/sounds/celebration.mp3',
  streak: '/sounds/streak.mp3',
  refresh: '/sounds/refresh.mp3',
  error: '/sounds/error.mp3',
  coin: '/sounds/coin.mp3',
  swoosh: '/sounds/swoosh.mp3',
};

// Storage key for preferences
const STORAGE_KEY = 'vault_feedback_preferences';

// Default preferences
const DEFAULT_PREFERENCES: FeedbackState = {
  soundEnabled: true,
  hapticEnabled: true,
  celebrationsEnabled: true,
  volume: 0.5,
  isAudioLoaded: false,
};

interface FeedbackProviderProps {
  children: ReactNode;
}

export const FeedbackProvider: React.FC<FeedbackProviderProps> = ({ children }) => {
  const [state, setState] = useState<FeedbackState>(() => {
    // Load preferences from localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          return { ...DEFAULT_PREFERENCES, ...parsed, isAudioLoaded: false };
        } catch {
          return DEFAULT_PREFERENCES;
        }
      }
    }
    return DEFAULT_PREFERENCES;
  });

  // Audio element refs
  const audioRefs = useRef<Map<SoundEffect, HTMLAudioElement>>(new Map());
  const audioContextRef = useRef<AudioContext | null>(null);

  // Initialize audio context and preload sounds
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Create audio context on first user interaction
    const initAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      // Preload all sound files
      Object.entries(SOUND_FILES).forEach(([effect, path]) => {
        const audio = new Audio(path);
        audio.preload = 'auto';
        audio.volume = state.volume;
        audioRefs.current.set(effect as SoundEffect, audio);
      });

      setState(prev => ({ ...prev, isAudioLoaded: true }));

      // Remove listener after first interaction
      document.removeEventListener('click', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };

    document.addEventListener('click', initAudio, { once: true });
    document.addEventListener('touchstart', initAudio, { once: true });

    return () => {
      document.removeEventListener('click', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };
  }, []);

  // Update volume for all audio elements when volume changes
  useEffect(() => {
    audioRefs.current.forEach(audio => {
      audio.volume = state.volume;
    });
  }, [state.volume]);

  // Save preferences to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { isAudioLoaded, ...prefs } = state;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    }
  }, [state.soundEnabled, state.hapticEnabled, state.celebrationsEnabled, state.volume]);

  // Play sound effect
  const playSound = useCallback((effect: SoundEffect) => {
    if (!state.soundEnabled) return;

    const audio = audioRefs.current.get(effect);
    if (audio) {
      // Clone and play to allow overlapping sounds
      const clone = audio.cloneNode() as HTMLAudioElement;
      clone.volume = state.volume;
      clone.play().catch(() => {
        // Ignore autoplay errors
      });
    }
  }, [state.soundEnabled, state.volume]);

  // Trigger haptic feedback
  const triggerHaptic = useCallback((type: HapticType) => {
    if (!state.hapticEnabled) return;
    if (typeof navigator === 'undefined' || !navigator.vibrate) return;

    // Vibration patterns (in milliseconds)
    const patterns: Record<HapticType, number | number[]> = {
      light: 10,
      medium: 25,
      heavy: 50,
      success: [10, 50, 10],
      warning: [25, 50, 25, 50, 25],
      error: [50, 100, 50],
      selection: 5,
    };

    try {
      navigator.vibrate(patterns[type]);
    } catch {
      // Ignore vibration errors
    }
  }, [state.hapticEnabled]);

  // Combined feedback helper
  const feedback = useCallback((sound: SoundEffect, haptic: HapticType) => {
    playSound(sound);
    triggerHaptic(haptic);
  }, [playSound, triggerHaptic]);

  // Sound controls
  const setSoundEnabled = useCallback((enabled: boolean) => {
    setState(prev => ({ ...prev, soundEnabled: enabled }));
  }, []);

  const setVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    setState(prev => ({ ...prev, volume: clampedVolume }));
  }, []);

  // Haptic controls
  const setHapticEnabled = useCallback((enabled: boolean) => {
    setState(prev => ({ ...prev, hapticEnabled: enabled }));
  }, []);

  // Celebration controls
  const setCelebrationsEnabled = useCallback((enabled: boolean) => {
    setState(prev => ({ ...prev, celebrationsEnabled: enabled }));
  }, []);

  const value: FeedbackContextType = {
    ...state,
    playSound,
    setSoundEnabled,
    setVolume,
    triggerHaptic,
    setHapticEnabled,
    setCelebrationsEnabled,
    feedback,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};

// Custom hook for common feedback patterns
export const useFeedbackPatterns = () => {
  const { feedback, playSound, triggerHaptic, celebrationsEnabled } = useFeedback();

  return {
    // Chart scrubbing
    onScrubTick: useCallback(() => {
      feedback('tick', 'light');
    }, [feedback]),

    // Button press
    onButtonPress: useCallback(() => {
      feedback('pop', 'medium');
    }, [feedback]),

    // Timeframe change
    onTimeframeChange: useCallback(() => {
      feedback('pop', 'selection');
    }, [feedback]),

    // Pull to refresh complete
    onRefreshComplete: useCallback(() => {
      feedback('refresh', 'success');
    }, [feedback]),

    // ATH celebration
    onATH: useCallback(() => {
      if (celebrationsEnabled) {
        playSound('celebration');
        triggerHaptic('success');
      }
    }, [playSound, triggerHaptic, celebrationsEnabled]),

    // Milestone achieved
    onMilestone: useCallback(() => {
      if (celebrationsEnabled) {
        playSound('success');
        triggerHaptic('success');
      }
    }, [playSound, triggerHaptic, celebrationsEnabled]),

    // Streak notification
    onStreak: useCallback(() => {
      if (celebrationsEnabled) {
        playSound('streak');
        triggerHaptic('medium');
      }
    }, [playSound, triggerHaptic, celebrationsEnabled]),

    // Value increase (counting)
    onValueTick: useCallback(() => {
      playSound('coin');
    }, [playSound]),

    // Error
    onError: useCallback(() => {
      feedback('error', 'error');
    }, [feedback]),
  };
};

export default FeedbackContext;
